import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import ProductCard from '@/components/ProductCard';import ProductDetailDialog from '@/components/ProductDetailDialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import FilterChips, { SKIN_TYPES, CONCERNS } from '@/components/Filters';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { Sparkles, Star, MapPin } from 'lucide-react';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';

const CATEGORY_LABELS = {
  cleanser: 'Cleanser',
  toner: 'Toner',
  serum: 'Serum',
  moisturizer: 'Moisturizer',
  oil: 'Face oil',
  exfoliant: 'Exfoliant',
  sunscreen: 'SPF',
  mask: 'Mask',
  eye: 'Eye care',
  lip: 'Lip',
  hair: 'Hair',
  nails: 'Nails',
  makeup: 'Makeup',
  perfume: 'Perfume',
  jewelry: 'Jewelry',
  watch: 'Watches',
};

export default function ProductCuratorPage() {
  const { user } = useAuth();
  const [tier, setTier] = useState('essential');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [skinType, setSkinType] = useState(user?.skin_type || null);
  const [concern, setConcern] = useState(null);
  const [category, setCategory] = useState(null);
  const [openProduct, setOpenProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [regions, setRegions] = useState([]);
  const [region, setRegion] = useState(user?.region || null);

  useEffect(() => {
    if (user?.skin_type && !skinType) setSkinType(user.skin_type);
    if (user?.region && !region) setRegion(user.region);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.skin_type, user?.region]);

  useEffect(() => {
    Promise.all([api.get('/categories'), api.get('/regions')]).then(([c, r]) => {
      setCategories(c.data);
      setRegions(r.data);
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = { tier };
        if (skinType) params.skin_type = skinType;
        if (concern) params.concern = concern;
        if (category) params.category = category;
        if (region) params.region = region;
        const { data } = await api.get('/products', { params });
        if (!cancelled) setProducts(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [tier, skinType, concern, category, region]);

  return (
    <div data-testid="product-curator-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <div className="max-w-2xl">
        <span className="text-[11px] uppercase tracking-[0.22em] text-rose-gold">Smart Product Curator</span>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-semibold text-ink leading-[1.05]">
          Two tiers. One thoughtful edit.
        </h1>
        <p className="mt-3 text-muted-foreground leading-relaxed">
          Skin, hair, lips, nails, perfume, jewelry, watches — only what we&rsquo;d truly recommend, sorted into{' '}
          <em className="text-foreground not-italic">Essential Picks</em> and{' '}
          <em className="text-foreground not-italic">Premium Luxe</em>.
        </p>
      </div>

      <div className="mt-8">
        <Tabs value={tier} onValueChange={setTier} data-testid="product-tier-tabs">
          <TabsList className="bg-secondary rounded-xl p-1 border border-border/70 grid grid-cols-2 max-w-md">
            <TabsTrigger
              value="essential"
              className="rounded-lg data-[state=active]:bg-card data-[state=active]:shadow-sm py-2.5"
              data-testid="product-tier-tab-essential"
            >
              <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Essential Picks
            </TabsTrigger>
            <TabsTrigger
              value="premium"
              className="rounded-lg data-[state=active]:bg-card data-[state=active]:shadow-sm py-2.5"
              data-testid="product-tier-tab-premium"
            >
              <Star className="h-3.5 w-3.5 mr-1.5" /> Premium Luxe
            </TabsTrigger>
          </TabsList>

          {/* Category chips */}
          <div className="mt-6">
            <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Category</p>
            <ScrollArea className="w-full">
              <ToggleGroup
                type="single"
                value={category || ''}
                onValueChange={(v) => setCategory(v || null)}
                className="flex gap-2 justify-start pb-2"
                data-testid="curator-category-toggle"
              >
                {categories.map((c) => (
                  <ToggleGroupItem
                    key={c}
                    value={c}
                    className="rounded-full px-3 py-1.5 h-auto text-[12px] tracking-[0.02em] border border-border/80 bg-background hover:bg-secondary data-[state=on]:bg-accent data-[state=on]:text-foreground data-[state=on]:border-rose-soft whitespace-nowrap"
                    data-testid={`curator-category-${c}`}
                  >
                    {CATEGORY_LABELS[c] || c}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-6">
            <FilterChips
              label="Skin Type"
              value={skinType}
              onChange={setSkinType}
              options={SKIN_TYPES}
              testId="curator-filter-skin-type"
            />
            <FilterChips
              label="Concern"
              value={concern}
              onChange={setConcern}
              options={CONCERNS}
              testId="curator-filter-concern"
            />
          </div>

          {/* Region selector */}
          <div className="mt-6 flex items-center gap-3 flex-wrap" data-testid="curator-region-row">
            <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-1">
              <MapPin className="h-3 w-3" /> My region
            </p>
            <ToggleGroup
              type="single"
              value={region || ''}
              onValueChange={(v) => setRegion(v || null)}
              className="flex flex-wrap gap-2"
            >
              {regions.map((r) => (
                <ToggleGroupItem
                  key={r.code}
                  value={r.code}
                  className="rounded-full px-3 py-1 h-auto text-[11px] tracking-[0.02em] border border-border/80 bg-background hover:bg-secondary data-[state=on]:bg-accent data-[state=on]:border-rose-soft"
                  data-testid={`curator-region-${r.code}`}
                >
                  {r.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            {region && (
              <button
                onClick={() => setRegion(null)}
                className="text-[11px] underline underline-offset-4 text-muted-foreground hover:text-foreground"
              >
                Show all
              </button>
            )}
          </div>

          <TabsContent value="essential" className="mt-8" data-testid="product-results-grid-essential">
            <ProductsGrid loading={loading} products={products} onOpen={setOpenProduct} />
          </TabsContent>
          <TabsContent value="premium" className="mt-8" data-testid="product-results-grid-premium">
            <ProductsGrid loading={loading} products={products} onOpen={setOpenProduct} />
          </TabsContent>
        </Tabs>
      </div>

      <ProductDetailDialog
        product={openProduct}
        open={!!openProduct}
        onOpenChange={(o) => !o && setOpenProduct(null)}
      />
    </div>
  );
}

const ProductsGrid = ({ loading, products, onOpen }) => {
  const PAGE = 9;
  const [visible, setVisible] = useState(PAGE);
  useEffect(() => { setVisible(PAGE); }, [products]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {[...Array(6)].map((_, i) => (
          <Skeleton key={i} className="h-96 rounded-2xl" />
        ))}
      </div>
    );
  }
  if (products.length === 0) {
    return (
      <div className="text-center py-16" data-testid="page-empty-state">
        <p className="font-display text-2xl text-ink">Nothing matches just yet.</p>
        <p className="text-sm text-muted-foreground mt-2">Try clearing a filter, switching tiers, or expanding region.</p>
      </div>
    );
  }
  return (
    <>
      <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-4">
        Showing {Math.min(visible, products.length)} of {products.length} {products.length === 1 ? 'product' : 'products'}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {products.slice(0, visible).map((p) => (
          <ProductCard key={p.id} product={p} onOpen={onOpen} />
        ))}
      </div>
      {visible < products.length && (
        <div className="mt-8 text-center">
          <Button
            variant="outline"
            className="rounded-xl"
            onClick={() => setVisible((v) => v + PAGE)}
            data-testid="curator-load-more-button"
          >
            Show more products
          </Button>
        </div>
      )}
    </>
  );
};
